import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

/**
 * Ingredient name with catalog autocomplete. Picking a suggestion links the
 * line to the shared ingredient (`ingredientId`); anything typed by hand stays
 * free text and the API creates or matches the ingredient on save.
 *
 * Uses a native <datalist>, which stays usable with a keyboard, a screen
 * reader and a phone keyboard without a hand-rolled combobox.
 */
export function IngredientNameInput({
  value,
  onChange,
  label,
  hideLabel = false,
}: {
  value: string;
  onChange: (name: string, ingredientId: string | null) => void;
  label: string;
  hideLabel?: boolean;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const [term, setTerm] = useState(value);
  const debounced = useDebouncedValue(term.trim(), 250);
  const search = useQuery({
    queryKey: ['ingredient-search', debounced],
    queryFn: () => api.ingredients.search(debounced, 8),
    enabled: debounced.length >= 2,
    staleTime: 5 * 60_000,
  });
  const items = search.data?.items ?? [];

  return (
    <div className="flex flex-1 flex-col gap-1">
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'text-sm font-medium text-ink'}>
        {label}
      </label>
      <input
        id={id}
        list={listId}
        value={value}
        autoComplete="off"
        placeholder="ingredient"
        onChange={(e) => {
          const next = e.target.value;
          setTerm(next);
          const hit = items.find(
            (i) => i.canonicalName.toLowerCase() === next.trim().toLowerCase(),
          );
          onChange(next, hit ? hit.id : null);
        }}
        className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
      />
      <datalist id={listId}>
        {items.map((item) => (
          <option key={item.id} value={item.canonicalName}>
            {item.matchedAlias ? `also “${item.matchedAlias}”` : (item.category ?? '')}
          </option>
        ))}
      </datalist>
    </div>
  );
}
